import __ from "src/utils/Translations";
import { FC, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Input, Label, Form, FormGroup, Button } from "reactstrap";
import { Stack, Box, Typography } from "@mui/material";
import Select from "react-select";
import useFetchEmploymentData from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/hooks/useFetchEmploymentData";
import { EmployeeFormValues } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/types/employeeTypes";
import { getContractTypeOptions } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/constants/contractTypes";
import { getEmploymentFormOptions } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/constants/employmentForms";
import DataPicker from "src/Components/FormElements/DatePicker";
import { useSaveEmploymentData } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/hooks/useSaveEmploymentData";
import { Loader } from "src/Components/Loader/Loader";
import { isDateValid } from "src/utils/jsHelpers/getDateAndTime";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import {
  cardStyle,
  headerBoxStyle,
  hoursBoxStyle,
  infoTextStyle,
  titleTextStyle,
} from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/forms/style";
import { organizationStructureEmploymentTabEdit } from "src/utils/RoleBasedSecurity/permissions";
import { EmployeeFormSchema } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/forms/EmployeeForm.scheme";
import formatHoursToText from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/utils/formatHoursToText";
import {
  validateNumericInput,
  validateDecimalInput,
} from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/utils/inputNumberValidator";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";

interface IEmployeeForm {
  employeeId: string;
  companyId: string;
  setIsEdited?: (isEdited: boolean, callback?: () => void) => void;
}

const EmployeeForm: FC<IEmployeeForm> = ({
  employeeId,
  companyId,
  setIsEdited,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
    clearErrors,
  } = useForm<EmployeeFormValues>({
    defaultValues: {
      contractFrom: null,
      contractTo: null,
      contractType: null,
      employmentForm: null,
      workingHoursFractionNominator: null,
      workingHoursFractionDenominator: null,
      dailyWorkingHours: "8.00",
    },
    resolver: standardSchemaResolver(EmployeeFormSchema),
  });

  useEffect(() => {
    if (setIsEdited) {
      setIsEdited(isDirty);
    }
  }, [isDirty, setIsEdited]);

  const { data } = useFetchEmploymentData(companyId, employeeId);
  const { mutate } = useSaveEmploymentData(companyId, employeeId);
  const isEditable: boolean = usePermission(
    organizationStructureEmploymentTabEdit
  );
  const employmentForms = getEmploymentFormOptions();
  const contractTypes = getContractTypeOptions();

  useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data, reset]);

  const contractFromDate = watch("contractFrom");
  const contractToDate = watch("contractTo");
  const dailyWorkingHours = watch("dailyWorkingHours");

  const onSubmit = (data: EmployeeFormValues) => {
    const formattedData = {
      ...data,
      contractFrom: data.contractFrom || null,
      contractTo: data.contractTo || null,
      workingHoursFractionDenominator: data.workingHoursFractionDenominator
        ? Number(data.workingHoursFractionDenominator)
        : null,
      workingHoursFractionNominator: data.workingHoursFractionNominator
        ? Number(data.workingHoursFractionNominator)
        : null,
      dailyWorkingHours: Number(data.dailyWorkingHours).toFixed(2),
    };

    mutate(formattedData, {
      onSuccess: () => {
        reset(undefined, { keepValues: true });

        if (setIsEdited) {
          setIsEdited(false);
        }
      },
    });
  };

  const handleDateChange = (
    fieldName: keyof EmployeeFormValues,
    date: Date | null
  ) => {
    if (date) {
      const localDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12,
        0,
        0
      );
      const formattedDate = localDate.toISOString().split("T")[0];

      if (
        fieldName === "contractFrom" &&
        contractToDate &&
        new Date(formattedDate) > new Date(contractToDate)
      ) {
        setValue("contractTo", null, { shouldDirty: true });
      }

      if (
        fieldName === "contractTo" &&
        contractFromDate &&
        new Date(formattedDate) < new Date(contractFromDate)
      ) {
        setValue("contractTo", null, { shouldDirty: true });
        return;
      }

      setValue(fieldName, formattedDate, { shouldDirty: true });
    } else {
      setValue(fieldName, null, { shouldDirty: true });
    }
  };

  return (
    <>
      <Box sx={headerBoxStyle}>
        <Typography sx={titleTextStyle}>
          {__("Zarządzanie zatrudnieniem")}
        </Typography>
        <Box>
          <Button
            type="button"
            color="primary"
            size="lg"
            style={{ marginRight: "8px" }}
            onClick={handleSubmit(onSubmit)}
            disabled={!isEditable}
          >
            {__("Zapisz")}
          </Button>
        </Box>
      </Box>
      <Box sx={cardStyle}>
        <Box sx={{ padding: "16px 20px" }}>
          <Typography
            sx={{
              ...infoTextStyle,
              textTransform: "uppercase",
              fontWeight: 700,
              color: "#12154EB2",
            }}
          >
            {__("Informacje o zatrudnieniu")}
          </Typography>
        </Box>
        <hr style={{ margin: 0 }} />
        <Box sx={{ padding: "20px" }}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Stack direction="column" sx={{ gap: 2 }}>
              <FormGroup style={{ marginBottom: 0 }}>
                <Box sx={{ width: "100%", maxWidth: "312px" }}>
                  <Controller
                    name="contractFrom"
                    control={control}
                    render={({ field }) => (
                      <DataPicker
                        id="contractFrom"
                        label={__("Data rozpoczęcia zatrudnienia")}
                        value={field.value ? new Date(field.value) : null}
                        onChange={(date: Date | null) =>
                          handleDateChange("contractFrom", date)
                        }
                        format="dd/MM/yyyy"
                        errorMessage={errors.contractFrom?.message}
                        validateField={(id: string, value: Date | null) => {
                          if (!isDateValid(value)) {
                            setValue(id as keyof EmployeeFormValues, null, {
                              shouldDirty: true,
                            });
                          }
                        }}
                        disabled={!isEditable}
                      />
                    )}
                  />
                </Box>
              </FormGroup>

              <FormGroup style={{ marginBottom: 0 }}>
                <Box sx={{ width: "100%" }}>
                  <Controller
                    name="contractTo"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Box sx={{ maxWidth: "312px" }}>
                          <DataPicker
                            id="contractTo"
                            label={__("Data zakończenia zatrudnienia")}
                            value={field.value ? new Date(field.value) : null}
                            onChange={(date: Date | null) =>
                              handleDateChange("contractTo", date)
                            }
                            format="dd/MM/yyyy"
                            errorMessage={errors.contractTo?.message}
                            minDate={
                              contractFromDate
                                ? new Date(contractFromDate)
                                : null
                            }
                            validateField={(id: string, value: Date | null) => {
                              if (!isDateValid(value)) {
                                setValue(id as keyof EmployeeFormValues, null);
                              }
                            }}
                            disabled={!isEditable}
                          />
                        </Box>
                        <Typography sx={infoTextStyle}>
                          {__(
                            "Jeżeli pracownik jest osobą zarządzającą obszarem to po dacie zakończenia zatrudnienia jego podległe obszary, zostaną przypisane do zarządców z nadrzędnych obszarów."
                          )}
                        </Typography>
                      </>
                    )}
                  />
                </Box>
              </FormGroup>

              <FormGroup style={{ marginBottom: 0 }}>
                <Label>{__("Rodzaj umowy")}</Label>
                <Controller
                  name="contractType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isClearable
                      isDisabled={!isEditable}
                      options={contractTypes}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      value={
                        contractTypes.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        field.onChange(
                          selectedOption ? selectedOption.value : null
                        )
                      }
                      placeholder={__("Wybierz")}
                    />
                  )}
                />
              </FormGroup>

              <FormGroup>
                <Label for="employmentForm">{__("Forma zatrudnienia")}</Label>
                <Controller
                  name="employmentForm"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isClearable
                      options={employmentForms}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      value={
                        employmentForms.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        field.onChange(
                          selectedOption ? selectedOption.value : null
                        )
                      }
                      placeholder={__("Wybierz")}
                      isDisabled={!isEditable}
                    />
                  )}
                />
              </FormGroup>

              <FormGroup style={{ marginBottom: 0 }}>
                <Label>{__("Wymiar etatu")}</Label>
                <Stack direction="row" sx={{ alignItems: "center" }}>
                  <Controller
                    name="workingHoursFractionNominator"
                    control={control}
                    render={({ field }) => (
                      <Box sx={{ width: "100px" }}>
                        <Input
                          type="number"
                          min={1}
                          {...field}
                          value={field.value ?? undefined}
                          onChange={(e) => {
                            const value = e.target.value
                              ? Number(e.target.value)
                              : null;
                            field.onChange(value);
                            clearErrors("workingHoursFraction");
                          }}
                          onKeyDown={(event) => validateNumericInput(event)}
                          invalid={
                            !!errors.workingHoursFractionNominator ||
                            !!errors.workingHoursFraction
                          }
                          disabled={!isEditable}
                        />
                      </Box>
                    )}
                  />
                  <Box sx={{ mx: 1 }}>/</Box>
                  <Controller
                    name="workingHoursFractionDenominator"
                    control={control}
                    render={({ field }) => (
                      <Box sx={{ width: "100px" }}>
                        <Input
                          type="number"
                          min={1}
                          {...field}
                          value={field.value ?? undefined}
                          onChange={(e) => {
                            const value = e.target.value
                              ? Number(e.target.value)
                              : null;
                            field.onChange(value);
                            clearErrors("workingHoursFraction");
                          }}
                          onKeyDown={(event) => validateNumericInput(event)}
                          invalid={
                            !!errors.workingHoursFractionDenominator ||
                            !!errors.workingHoursFraction
                          }
                          disabled={!isEditable}
                        />
                      </Box>
                    )}
                  />
                  <Box sx={{ mx: 1 }}>{__("etatu")}</Box>
                </Stack>

                {typeof errors.workingHoursFraction?.message === "string" && (
                  <Typography
                    color="error"
                    sx={{
                      marginTop: 1,
                      fontSize: "14px",
                      lineHeight: "21px",
                    }}
                  >
                    {errors.workingHoursFraction.message}
                  </Typography>
                )}
                <Typography sx={{ ...infoTextStyle, fontSize: "12px", mt: 1 }}>
                  {__("Pole umożliwia dodawanie wartości w góre i w dół o 1")}
                </Typography>
              </FormGroup>

              <FormGroup style={{ marginBottom: 0 }}>
                <Label>
                  <Typography sx={infoTextStyle}>
                    {__("Dzienny wymiar czasu pracy (w godzinach)")}:
                  </Typography>
                </Label>
                <Stack direction="row" sx={{ alignItems: "center", mt: 1 }}>
                  <Box sx={{ width: "100%", maxWidth: "100px" }}>
                    <Controller
                      name="dailyWorkingHours"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          step="1"
                          min={0}
                          max={24}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          onKeyDown={(event) => validateDecimalInput(event)}
                          invalid={!!errors.dailyWorkingHours}
                          disabled={!isEditable}
                        />
                      )}
                    />
                  </Box>

                  {dailyWorkingHours && (
                    <Box sx={hoursBoxStyle}>
                      {`${__("czyli")} ${formatHoursToText(
                        Number(dailyWorkingHours)
                      )}`}
                    </Box>
                  )}
                </Stack>
                {errors.dailyWorkingHours && (
                  <Typography
                    color="error"
                    sx={{
                      marginTop: 1,
                      fontSize: "14px",
                      lineHeight: "21px",
                    }}
                  >
                    {errors.dailyWorkingHours.message}
                  </Typography>
                )}
                <Typography sx={{ ...infoTextStyle, fontSize: "12px", mt: 1 }}>
                  {__(
                    "Należy wprowadzić wartość liczbową w zakresie 0,01-24,00."
                  )}
                </Typography>
                <Typography sx={{ ...infoTextStyle, fontSize: "12px" }}>
                  {__(
                    "Wartość wprowadzona w tym polu wpłynie na liczbę godzin pojedynczego wniosku o nieobecność pracownika."
                  )}
                </Typography>
              </FormGroup>
            </Stack>
          </Form>
        </Box>
      </Box>
    </>
  );
};

export default EmployeeForm;
